import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, MapPinIcon, WarehouseIcon, TruckIcon } from 'lucide-react';
import { OrderMaterialsList } from './OrderMaterialsList';
import { MaterialScanningInterface } from './MaterialScanningInterface';
import { PreparationProgress } from './PreparationProgress';
interface MaterialPreparationProps {
  onBackToOverview?: () => void;
}
export interface MaterialItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  location: string;
  zone: string;
  pallets: number;
  scanned: number;
  status: 'pending' | 'in-progress' | 'completed';
}
export interface PreparationOrder {
  id: string;
  orderId: string;
  recipeName: string;
  materials: MaterialItem[];
  status: 'pending' | 'in-progress' | 'completed';
  deadline: Date;
}
export function MaterialPreparation({
  onBackToOverview
}: MaterialPreparationProps) {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedOrder, setSelectedOrder] = useState<PreparationOrder | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialItem | null>(null);
  const [preparationOrders, setPreparationOrders] = useState<PreparationOrder[]>([{
    id: 'PREP001',
    orderId: '1001',
    recipeName: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(new Date().setHours(10, 0, 0, 0)),
    materials: [{
      id: 'R061307',
      name: 'MSC Alaska Seelachs',
      quantity: 0.625,
      unit: 'Tonnen',
      location: 'TK-Lager Reihe 3',
      zone: 'A4',
      pallets: 3,
      scanned: 0,
      status: 'pending'
    }, {
      id: 'R053408',
      name: 'Hähnchen Brust St gebraten',
      quantity: 0.375,
      unit: 'Tonnen',
      location: 'TK-Lager Reihe 5',
      zone: 'B2',
      pallets: 2,
      scanned: 0,
      status: 'pending'
    }, {
      id: 'R161215',
      name: 'Langkornreis',
      quantity: 0.75,
      unit: 'Tonnen',
      location: 'Trockenlager Reihe 2',
      zone: 'C3',
      pallets: 4,
      scanned: 0,
      status: 'pending'
    }, {
      id: 'R090533',
      name: 'Erbsen',
      quantity: 0.25,
      unit: 'Tonnen',
      location: 'TK-Lager Reihe 1',
      zone: 'A1',
      pallets: 2,
      scanned: 0,
      status: 'pending'
    }, {
      id: 'R092017',
      name: 'Tomate',
      quantity: 0.125,
      unit: 'Tonnen',
      location: 'Kühlraum Reihe 4',
      zone: 'D2',
      pallets: 1,
      scanned: 0,
      status: 'pending'
    }]
  }, {
    id: 'PREP002',
    orderId: '1002',
    recipeName: 'Gustoria Risotto',
    status: 'pending',
    deadline: new Date(new Date().setHours(13, 0, 0, 0)),
    materials: [{
      id: 'R161215',
      name: 'Langkornreis',
      quantity: 0.9,
      unit: 'Tonnen',
      location: 'Trockenlager Reihe 2',
      zone: 'C3',
      pallets: 5,
      scanned: 0,
      status: 'pending'
    }, {
      id: 'R092616',
      name: 'Zwiebeln',
      quantity: 0.18,
      unit: 'Tonnen',
      location: 'Kühlraum Reihe 2',
      zone: 'D1',
      pallets: 1,
      scanned: 0,
      status: 'pending'
    }, {
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      quantity: 0.09,
      unit: 'Tonnen',
      location: 'Öllager Reihe 1',
      zone: 'E1',
      pallets: 1,
      scanned: 0,
      status: 'pending'
    }]
  }]);
  const handleOrderSelect = (order: PreparationOrder) => {
    setSelectedOrder(order);
    setView('detail');
  };
  const handleBackToList = () => {
    setView('list');
    setSelectedOrder(null);
    setSelectedMaterial(null);
  };
  const handleMaterialSelect = (material: MaterialItem) => {
    setSelectedMaterial(material);
  };
  const handleScanComplete = (materialId: string, scannedCount: number) => {
    if (!selectedOrder) return;
    // Update the scanned count for the material
    const updatedOrders = preparationOrders.map(order => {
      if (order.id === selectedOrder.id) {
        const updatedMaterials = order.materials.map(material => {
          if (material.id === materialId) {
            const newScannedCount = material.scanned + scannedCount;
            const newStatus = newScannedCount >= material.pallets ? 'completed' : 'in-progress';
            return {
              ...material,
              scanned: newScannedCount,
              status: newStatus
            };
          }
          return material;
        });
        // Check if all materials are completed
        const allCompleted = updatedMaterials.every(m => m.status === 'completed');
        return {
          ...order,
          materials: updatedMaterials,
          status: allCompleted ? 'completed' : 'in-progress'
        };
      }
      return order;
    });
    setPreparationOrders(updatedOrders);
    // Update the selected order and material
    const updatedOrder = updatedOrders.find(o => o.id === selectedOrder.id);
    if (updatedOrder) {
      setSelectedOrder(updatedOrder);
      const updatedMaterial = updatedOrder.materials.find(m => m.id === materialId);
      if (updatedMaterial) {
        setSelectedMaterial(updatedMaterial);
      } else {
        setSelectedMaterial(null);
      }
    }
  };
  // Handler für den automatischen Rücksprung zur Übersicht
  const handleOrderComplete = () => {
    // Zurück zur Übersicht
    handleBackToList();
  };
  return <div className="container mx-auto" data-id="element-552">
      <div className="mb-8" data-id="element-553">
        <div data-id="element-554">
          <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-555">
            Materialbereitstellung
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-556">
            Bereitstellung und Vorbereitung von Rohstoffen für die Produktion
          </p>
          {onBackToOverview}
        </div>
      </div>
      {view === 'list' ? <div className="bg-white rounded-xl shadow-md p-6" data-id="element-557">
          <div className="mb-6" data-id="element-558">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-559">
              Heutige Bereitstellungsaufträge
            </h3>
            <p className="text-gray-600" data-id="element-560">
              Wählen Sie einen Auftrag aus, um mit der Bereitstellung zu
              beginnen
            </p>
          </div>
          <div className="space-y-4" data-id="element-561">
            {preparationOrders.map(order => <div key={order.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${order.status === 'completed' ? 'bg-green-50 border-green-200' : order.status === 'in-progress' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} onClick={() => handleOrderSelect(order)} data-id="element-562">
                <div className="flex justify-between items-start" data-id="element-563">
                  <div data-id="element-564">
                    <h4 className="font-medium text-gray-900" data-id="element-565">
                      {order.recipeName}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-566">
                      Auftrags-Nr: #{order.orderId}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-567">
                      Bereitstellung bis:{' '}
                      {order.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                      Uhr
                    </p>
                  </div>
                  <div className="flex items-center" data-id="element-568">
                    {order.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-569">
                        <CheckCircleIcon size={12} className="mr-1" data-id="element-570" />
                        Abgeschlossen
                      </span> : order.status === 'in-progress' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-571">
                        <PackageIcon size={12} className="mr-1" data-id="element-572" />
                        In Bearbeitung
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-573">
                        <TruckIcon size={12} className="mr-1" data-id="element-574" />
                        Ausstehend
                      </span>}
                  </div>
                </div>
                <div className="mt-3" data-id="element-575">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-576">
                    Fortschritt:{' '}
                    {order.materials.filter(m => m.status === 'completed').length}{' '}
                    von {order.materials.length} Materialien bereitgestellt
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-577">
                    <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
                width: `${order.materials.filter(m => m.status === 'completed').length / order.materials.length * 100}%`
              }} data-id="element-578"></div>
                  </div>
                </div>
              </div>)}
            {preparationOrders.length === 0 && <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-579">
                <PackageIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-580" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-581">
                  Keine Bereitstellungsaufträge
                </h3>
                <p className="text-gray-500" data-id="element-582">
                  Für heute sind keine Produktionsaufträge geplant, die eine
                  Bereitstellung erfordern.
                </p>
              </div>}
          </div>
        </div> : <div className="bg-white rounded-xl shadow-md p-6" data-id="element-583">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-584">
            <ArrowLeftIcon size={16} data-id="element-585" />
            <span data-id="element-586">Zurück zur Übersicht</span>
          </button>
          {selectedOrder && <>
              <div className="mb-6" data-id="element-587">
                <h3 className="text-xl font-bold text-gray-800" data-id="element-588">
                  Bereitstellung: {selectedOrder.recipeName}
                </h3>
                <p className="text-gray-600" data-id="element-589">
                  Auftrags-Nr: #{selectedOrder.orderId} | Bereitstellung bis:{' '}
                  {selectedOrder.deadline.toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            })}{' '}
                  Uhr
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-590">
                <div className="lg:col-span-1" data-id="element-591">
                  <PreparationProgress order={selectedOrder} onComplete={handleOrderComplete} data-id="element-592" />
                </div>
                <div className="lg:col-span-2" data-id="element-593">
                  {selectedMaterial ? <MaterialScanningInterface material={selectedMaterial} onScanComplete={count => handleScanComplete(selectedMaterial.id, count)} onBack={() => setSelectedMaterial(null)} data-id="element-594" /> : <OrderMaterialsList materials={selectedOrder.materials} onMaterialSelect={handleMaterialSelect} data-id="element-595" />}
                </div>
              </div>
            </>}
        </div>}
    </div>;
}